import { randomizeDisplayAmount } from './amountRandomizer';
import { formatInstantDate } from './dateTimeUtils';

export const MOCK_CURRENCY_SYMBOL = '₶';

interface FormatCurrencyOptions {
    randomize?: boolean;
    currency?: string;
    compact?: boolean;
    showAbsolute?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
}

// Basic currency formatter (e.g., INR)
export const formatCurrency = (
    amount: number | undefined | null,
    options: FormatCurrencyOptions = {},
): string => {
    if (amount === undefined || amount === null) {
        return options.randomize ? `${MOCK_CURRENCY_SYMBOL}--.--` : '₹--.--';
    }

    const displayAmount = options.randomize ? randomizeDisplayAmount(amount) : amount;
    const finalAmount = options.showAbsolute ? Math.abs(displayAmount) : displayAmount;

    if (options.randomize) {
        const absoluteAmount = Math.abs(finalAmount);
        const sign = finalAmount < 0 ? '-' : '';

        const numberPortion = options.compact && absoluteAmount >= 1000
            ? new Intl.NumberFormat('en-IN', {
                notation: 'compact',
                compactDisplay: 'short',
                maximumFractionDigits: options.maximumFractionDigits ?? 1,
                minimumFractionDigits: options.minimumFractionDigits ?? (absoluteAmount % 1000 !== 0 ? 1 : 0),
            }).format(absoluteAmount)
            : new Intl.NumberFormat('en-IN', {
                minimumFractionDigits: options.minimumFractionDigits ?? 0,
                maximumFractionDigits: options.maximumFractionDigits ?? (options.compact ? 0 : 2),
            }).format(absoluteAmount);

        return `${sign}${MOCK_CURRENCY_SYMBOL}${numberPortion}`;
    }

    if (options.compact && Math.abs(finalAmount) >= 1000) {
        return new Intl.NumberFormat('en-IN', {
            notation: 'compact',
            compactDisplay: 'short',
            maximumFractionDigits: options.maximumFractionDigits ?? 1,
            minimumFractionDigits: options.minimumFractionDigits ?? (finalAmount % 1000 !== 0 ? 1 : 0),
        }).format(finalAmount);
    }

    // Use Intl.NumberFormat for proper localization and formatting
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: options.currency ?? 'INR',
        minimumFractionDigits: options.minimumFractionDigits ?? 0, // Adjust as needed, image shows no decimals
        maximumFractionDigits: options.maximumFractionDigits ?? (options.compact ? 0 : 2),
    }).format(finalAmount);
};

// Basic date formatter
export const formatDate = (dateString: string | undefined | null, includeTime: boolean = false): string => {
    try {
        return formatInstantDate(dateString, includeTime);
    } catch (error) {
        console.error("Error formatting date:", dateString, error);
        return 'Invalid Date';
    }
};

// You can add more specific formatters as needed 
